import {
  getCsvValues,
  getPositiveIntegerValues,
  getValidTimestamp,
  HydratedUrlFilters,
  isSafeOptionValue,
  toCanonicalSearch,
  uniqueValues,
} from 'views/bpaUrlFilterUtils';

import { FilterValues } from './types/bpaFindings.types';

export type FindingsFrameworkFilter = 'all' | number;

export const BPA_FINDINGS_DATE_FILTER_KEYS = [
  'first_seen_at',
  'last_seen_at',
  'last_fixed_at',
  'created_at',
  'ignored_date',
] as const;

const TEXT_LIST_FILTER_KEYS = [
  'benchmark',
  'region',
  'provider',
  'module',
] as const;

const ASSIGNMENT_QUERY_KEYS = [
  'assigned',
  'self_assigned',
  'self_watching',
  'unread_notes',
] as const;

const AGE_QUERY_KEYS = [
  'age_min',
  'age_max',
  'age_min_exclusive',
  'age_max_exclusive',
] as const;

const SCAN_STATUS_MAP: Record<string, string> = {
  open: 'Active',
  active: 'Active',
  fixed: 'Fixed',
  ignored: 'Ignored',
};

export const getDefaultBPAFindingsFilters = (): FilterValues => ({
  scan_status: ['Active'],
});

const getLastValue = (params: URLSearchParams, key: string): string | null => {
  const values = params.getAll(key);
  return values.length > 0 ? values[values.length - 1] : null;
};

export const parseBPAFindingsUrlFilters = (
  search: string,
): HydratedUrlFilters<FilterValues, FindingsFrameworkFilter> => {
  const params = new URLSearchParams(search);
  const canonicalParams = new URLSearchParams();
  const queryFilters: FilterValues = {};
  let frameworkId: FindingsFrameworkFilter = 'all';
  let hasValidUrlFilters = false;

  const markValid = () => {
    hasValidUrlFilters = true;
  };

  const searchValue = getLastValue(params, 'search')?.trim();
  if (searchValue && searchValue.length <= 250) {
    queryFilters.search = searchValue;
    canonicalParams.set('search', searchValue);
    markValid();
  }

  const controlIds = getPositiveIntegerValues(
    getCsvValues(params, 'control_id'),
  );
  if (controlIds.length > 0) {
    queryFilters.control_id = controlIds;
    canonicalParams.set('control_id', controlIds.join(','));
    markValid();
  }

  const severityValues = uniqueValues(
    getCsvValues(params, 'severity').filter((value) =>
      ['1', '2', '3', '4'].includes(value),
    ),
  );
  if (severityValues.length > 0) {
    queryFilters.severity = severityValues;
    canonicalParams.set('severity', severityValues.join(','));
    markValid();
  }

  const rawScanStatuses = getCsvValues(params, 'scan_status');
  const rawUserStatuses = getCsvValues(params, 'user_status');
  const explicitlyAllStatuses =
    params.has('scan_status') &&
    (rawScanStatuses.length === 0 ||
      rawScanStatuses.some((value) => value.toLowerCase() === 'all'));
  const scanStatuses = rawScanStatuses
    .map((value) => SCAN_STATUS_MAP[value.toLowerCase()])
    .filter((value): value is string => Boolean(value));

  if (rawUserStatuses.some((value) => value.toLowerCase() === 'ignored')) {
    scanStatuses.push('Ignored');
  }

  const normalizedStatuses = uniqueValues(scanStatuses);
  if (normalizedStatuses.length > 0) {
    // Hydrate BPA's grouped closed option as one ordinary Status selection.
    queryFilters.scan_status =
      normalizedStatuses.length === 2 &&
      normalizedStatuses.includes('Fixed') &&
      normalizedStatuses.includes('Ignored')
        ? ['Fixed,Ignored']
        : normalizedStatuses;
    canonicalParams.set('scan_status', normalizedStatuses.join(','));
    markValid();
  } else if (explicitlyAllStatuses) {
    queryFilters.scan_status = [''];
    canonicalParams.set('scan_status', 'all');
    markValid();
  }

  const assignmentFilters = ASSIGNMENT_QUERY_KEYS.filter((key) =>
    params.getAll(key).some((value) => value.toLowerCase() === 'true'),
  ).map((key) => `${key}=true`);
  if (assignmentFilters.length > 0) {
    queryFilters.assignment_filters = assignmentFilters;
    assignmentFilters.forEach((filter) => {
      canonicalParams.set(filter.replace('=true', ''), 'true');
    });
    markValid();
  }

  TEXT_LIST_FILTER_KEYS.forEach((key) => {
    const values = uniqueValues(
      getCsvValues(params, key).filter((value) => isSafeOptionValue(value)),
    );
    if (values.length > 0) {
      queryFilters[key] = values;
      canonicalParams.set(key, values.join(','));
      markValid();
    }
  });

  const integrationIds = getPositiveIntegerValues([
    ...getCsvValues(params, 'integration'),
    ...getCsvValues(params, 'integration_ids'),
  ]);
  if (integrationIds.length > 0) {
    queryFilters.integration_ids = integrationIds;
    canonicalParams.set('integration', integrationIds.join(','));
    markValid();
  }

  BPA_FINDINGS_DATE_FILTER_KEYS.forEach((key) => {
    const fromKey = `dt_filter__${key}__from`;
    const toKey = `dt_filter__${key}__to`;
    const from = getValidTimestamp(getLastValue(params, fromKey));
    const to = getValidTimestamp(getLastValue(params, toKey));

    if (from === null && to === null) return;
    if (from !== null && to !== null && from > to) return;

    queryFilters[key] = [{ from, to }];
    if (from !== null) canonicalParams.set(fromKey, String(from));
    if (to !== null) canonicalParams.set(toKey, String(to));
    markValid();
  });

  const ageRange = {
    minValue: undefined as number | undefined,
    maxValue: undefined as number | undefined,
    isMinExclusive: false,
    isMaxExclusive: false,
  };
  let hasAgeValue = false;

  AGE_QUERY_KEYS.forEach((key) => {
    // Also accept the double-underscore form used by the shared age-filter
    // URL convention, while emitting the BPA API's scalar parameter names.
    const rawValue =
      getLastValue(params, key) ??
      getLastValue(params, key.replace('age_', 'age__'));
    if (rawValue === null || !/^\d+$/.test(rawValue)) return;

    const value = Number(rawValue);
    if (!Number.isSafeInteger(value)) return;

    hasAgeValue = true;
    canonicalParams.set(key, String(value));
    if (key === 'age_min' || key === 'age_min_exclusive') {
      ageRange.minValue = value;
      ageRange.isMinExclusive = key === 'age_min_exclusive';
    } else {
      ageRange.maxValue = value;
      ageRange.isMaxExclusive = key === 'age_max_exclusive';
    }
  });

  if (hasAgeValue) {
    queryFilters.age = [ageRange];
    markValid();
  }

  const frameworkValue = getLastValue(params, 'framework_id');
  if (frameworkValue && /^\d+$/.test(frameworkValue)) {
    const parsedFrameworkId = Number(frameworkValue);
    if (Number.isSafeInteger(parsedFrameworkId) && parsedFrameworkId > 0) {
      frameworkId = parsedFrameworkId;
      canonicalParams.set('framework_id', String(parsedFrameworkId));
      markValid();
    }
  }

  return {
    filterValues: hasValidUrlFilters
      ? queryFilters
      : getDefaultBPAFindingsFilters(),
    routeFilter: frameworkId,
    hasValidUrlFilters,
    canonicalSearch: toCanonicalSearch(canonicalParams),
  };
};
